<template>
  <div class="auth-page">
    <div class="auth-shell">
      <div class="auth-shell__brand">
        <div class="auth-brand__header">
          <div class="auth-brand__logo">
            <img src="@/assets/logo.png" alt="BISU RIE System logo" />
          </div>
          <div class="auth-brand__label">
            <span>Digital Platform</span>
            <span>RIE System</span>
          </div>
        </div>

        <div>
          <h1 class="auth-brand__title">
            <span>Research, Innovation</span>
            <span class="accent">& Extension</span>
          </h1>
          <p class="auth-brand__description">
            Empowering BISU faculty to track, manage, and showcase research outputs, projects, and extension activities.
          </p>
        </div>

        <div class="auth-brand__stats">
          <div class="auth-brand__stat">
            <span class="auth-brand__stat-figure">500+</span>
            <span class="auth-brand__stat-caption">Research Papers</span>
          </div>
          <div class="auth-brand__stat">
            <span class="auth-brand__stat-figure">150+</span>
            <span class="auth-brand__stat-caption">Faculty Members</span>
          </div>
          <div class="auth-brand__stat">
            <span class="auth-brand__stat-figure">80+</span>
            <span class="auth-brand__stat-caption">Extension Programs</span>
          </div>
        </div>
      </div>

      <div class="auth-shell__content">
        <form class="auth-form" @submit.prevent="handleForgotPassword">
          <button class="auth-arrow-link" type="button" @click="handleSignIn">← Back to sign in</button>

          <div class="auth-form__header">
            <h2 class="main-title">Reset password</h2>
            <p class="subtitle" v-if="step === 1">Enter your email to find your account.</p>
            <p class="subtitle" v-else-if="step === 2">Confirm this is your account before continuing.</p>
            <p class="subtitle" v-else>Set your new password.</p>
          </div>

          <!-- Step 1: Email Input -->
          <div v-if="step === 1" class="form-group">
            <label class="form-label" for="email">Email *</label>
            <input
              type="email"
              id="email"
              class="form-input"
              v-model="form.email"
              placeholder="Enter your email"
              :class="{ 'error': errors.email }"
            />
            <span v-if="errors.email" class="small-note error-message">{{ errors.email }}</span>
          </div>

          <!-- Step 2: Account Preview -->
          <div v-if="step === 2" class="form-feedback" style="margin-bottom: 1rem;">
            <div class="success-message" style="display:block;">
              <strong>Account found</strong><br />
              Full name: {{ accountPreview?.fullname || 'N/A' }}<br />
              Email: {{ accountPreview?.email || form.email }}
            </div>
          </div>

          <!-- Step 3: Password Inputs -->
          <div v-if="step === 3" class="form-feedback" style="margin-bottom: 1rem;">
            <div class="success-message" style="display:block;">
              Resetting password for: <strong>{{ accountPreview?.fullname || form.email }}</strong>
            </div>
          </div>

          <div v-if="step === 3" class="form-group">
            <label class="form-label" for="newPassword">New Password *</label>
            <input
              type="password"
              id="newPassword"
              class="form-input"
              v-model="form.newPassword"
              placeholder="At least 8 characters"
              :class="{ 'error': errors.newPassword }"
            />
            <span v-if="errors.newPassword" class="small-note error-message">{{ errors.newPassword }}</span>
          </div>

          <div v-if="step === 3" class="form-group">
            <label class="form-label" for="confirmPassword">Confirm Password *</label>
            <input
              type="password"
              id="confirmPassword"
              class="form-input"
              v-model="form.confirmPassword"
              placeholder="Re-enter password"
              :class="{ 'error': errors.confirmPassword }"
            />
            <span v-if="errors.confirmPassword" class="small-note error-message">{{ errors.confirmPassword }}</span>
          </div>

          <button v-if="step > 1" type="button" class="custom-button" style="margin-bottom: 0.75rem;" @click="goBackStep">
            Back
          </button>

          <button type="submit" class="custom-button primary" :disabled="isLoading">
            <span v-if="isLoading" class="spinner"></span>
            {{ isLoading ? 'Please wait...' : primaryButtonText }}
          </button>

          <!-- Global Messages -->
          <div v-if="error || success" class="form-feedback">
            <div v-if="error" class="error-message">{{ error }}</div>
            <div v-if="success" class="success-message">{{ success }}</div>
          </div>

          <!-- Links -->
          <div class="auth-form__links">
            Remembered your password?
            <a href="#" @click.prevent="handleSignIn">Sign in</a>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/apiClient'

export default {
  name: 'ForgotPassword',
  data() {
    return {
      step: 1,
      accountPreview: null,
      form: {
        email: '',
        newPassword: '',
        confirmPassword: ''
      },
      errors: {
        email: '',
        newPassword: '',
        confirmPassword: ''
      },
      error: '',
      success: '',
      isLoading: false
    }
  },

  computed: {
    primaryButtonText() {
      if (this.step === 1) return 'Next';
      if (this.step === 2) return 'This is my account';
      return 'Reset Password';
    }
  },

  methods: {
    clearErrors() {
      this.errors.email = '';
      this.errors.newPassword = '';
      this.errors.confirmPassword = '';
      this.error = '';
      this.success = '';
    },
    validateEmailStep() {
      this.clearErrors();
      let isValid = true;
      if (!this.form.email.trim()) {
        this.errors.email = 'Email is required';
        isValid = false;
      } else if (!this.isValidEmail(this.form.email)) {
        this.errors.email = 'Invalid email format';
        isValid = false;
      }

      return isValid;
    },

    validatePasswordStep() {
      this.clearErrors();
      let isValid = true;
      if (!this.form.newPassword.trim()) {
        this.errors.newPassword = 'New password is required';
        isValid = false;
      } else if (this.form.newPassword.length < 8) {
        this.errors.newPassword = 'Password must be at least 8 characters';
        isValid = false;
      }

      if (!this.form.confirmPassword.trim()) {
        this.errors.confirmPassword = 'Please confirm your password';
        isValid = false;
      } else if (this.form.newPassword !== this.form.confirmPassword) {
        this.errors.confirmPassword = 'Passwords do not match';
        isValid = false;
      }

      return isValid;
    },
    isValidEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return re.test(email);
    },
    goBackStep() {
      if (this.step > 1) this.step -= 1;
      this.error = '';
      this.success = '';
    },
    async handleForgotPassword() {
      if (this.step === 1) {
        if (!this.validateEmailStep()) return;
        this.isLoading = true;
        this.clearErrors();
        try {
          const { data } = await api.post('/forgot-password/identify', {
            email: this.form.email
          });
          this.accountPreview = data?.profile || null;
          this.step = 2;
        } catch (err) {
          this.error = err?.response?.data?.message || 'Account lookup failed. Please try again.';
        } finally {
          this.isLoading = false;
        }
        return;
      }

      if (this.step === 2) {
        this.step = 3;
        this.error = '';
        this.success = '';
        return;
      }

      if (!this.validatePasswordStep()) return;

      this.isLoading = true;
      this.clearErrors();
      try {
        const { data } = await api.post('/forgot-password', {
          email: this.form.email,
          newPassword: this.form.newPassword,
          confirmPassword: this.form.confirmPassword
        });

        if (data.success) {
          this.success = data.message || 'Password reset successful!';
          this.step = 1;
          this.accountPreview = null;
          this.form.newPassword = '';
          this.form.confirmPassword = '';
        } else {
          this.error = data.message || 'Failed to reset password.';
        }
      } catch (err) {
        this.error = err?.response?.data?.message || 'Something went wrong. Please try again.';
      } finally {
        this.isLoading = false;
      }
    },

    handleSignIn() {
      this.$router.push({ name: 'SignIn' });
    }
  }
}
</script>

<style scoped>
.auth-shell__content {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: clamp(2.5rem, 7vw, 5rem) clamp(1.25rem, 4vw, 3.5rem);
}

.auth-form {
  width: min(100%, 680px);
  margin: 0 auto;
  padding: 0;
  background: none;
  border: none;
  box-shadow: none;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  opacity: 1;
  transform: none;
  animation: none;
}

.auth-form__header .main-title {
  font-size: clamp(2.1rem, 3.8vw, 3rem);
  line-height: 1.05;
  margin-bottom: 0.35rem;
  color: #f8fbff;
  text-shadow: none;
}

.auth-form__header .subtitle {
  color: rgba(186, 201, 230, 0.9);
  margin-top: 0;
}

.form-label {
  color: rgba(214, 226, 250, 0.9);
}

.auth-form .form-input {
  background: rgba(10, 22, 40, 0.72);
  border: 1px solid rgba(83, 112, 160, 0.45);
  color: rgba(235, 242, 255, 0.92);
}

.auth-form .form-input::placeholder {
  color: rgba(186, 201, 230, 0.55);
}

.auth-form .form-input:focus {
  border-color: rgba(56, 189, 248, 0.78);
  box-shadow: 0 0 0 2px rgba(56, 189, 248, 0.24);
  background: rgba(14, 27, 45, 0.9);
}

.password-field {
  position: relative;
  width: 100%;
}

.password-field .form-input {
  width: 100%;
  padding-right: 2.75rem;
}

.password-toggle {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  display: inline-grid;
  place-items: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: rgba(226, 232, 255, 0.7);
  cursor: pointer;
  line-height: 0;
  z-index: 2;
}

.password-toggle:hover {
  color: #eff6ff;
}

.password-toggle:focus-visible {
  outline: 2px solid rgba(125, 211, 252, 0.55);
  outline-offset: 2px;
}

.auth-form__links {
  color: rgba(186, 201, 230, 0.72);
}

.auth-form__links a {
  color: #7dd3fc;
  font-weight: 600;
}

.auth-form__links a:hover {
  color: #bae6fd;
}

.custom-button.primary {
  box-shadow: 0 18px 34px rgba(14, 116, 144, 0.35);
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.custom-button.primary:hover:not(:disabled) {
  transform: translateY(-3px);
  box-shadow: 0 10px 24px rgba(56, 189, 248, 0.16);
}

.custom-button.primary:active:not(:disabled) {
  transform: translateY(-1px) scale(0.995);
}

.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.18);
  border-top-color: #38bdf8;
  animation: spin 0.9s linear infinite;
  margin-right: 8px;
  vertical-align: middle;
}

@keyframes spin { to { transform: rotate(360deg); } }

.form-feedback {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.form-feedback .error-message {
  background: rgba(110, 29, 43, 0.55);
  border: 1px solid rgba(255, 120, 140, 0.25);
  box-shadow: inset 0 0 0 1px rgba(255, 120, 140, 0.1);
  color: rgba(255, 132, 132, 0.86);
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.75rem;
  line-height: 1.5;
}

.form-feedback .success-message {
  background: rgba(30, 85, 66, 0.55);
  border: 1px solid rgba(96, 214, 173, 0.3);
  box-shadow: inset 0 0 0 1px rgba(96, 214, 173, 0.18);
  color: rgba(96, 214, 173, 0.86);
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.75rem;
  line-height: 1.5;
}

.success-message {
  color: rgba(96, 214, 173, 0.86);
}

.error-message {
  color: rgba(255, 132, 132, 0.86);
}

.small-note.error-message {
  color: rgba(255, 132, 132, 0.86);
  padding: 0;
  background: none;
  border: none;
  box-shadow: none;
  margin-top: 4px;
  font-size: 0.75rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 8px;
}

.custom-button {
  width: 100%;
  padding: 8px 24px;
  border-radius: 999px;
  font-weight: 500;
  font-size: 0.9375rem;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.custom-button:not(.primary) {
  background: rgba(56, 189, 248, 0.14);
  color: rgba(220, 231, 255, 0.88);
  border: 1px solid rgba(125, 211, 252, 0.44);
  border-radius: 999px;
}

.custom-button:not(.primary):hover:not(:disabled) {
  background: rgba(56, 189, 248, 0.24);
  border-color: rgba(125, 211, 252, 0.55);
}

.custom-button.primary {
  background: linear-gradient(135deg, #14b8a6 0%, #0ea5e9 58%, #2563eb 100%);
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 18px 34px rgba(14, 116, 144, 0.3);
}

.custom-button.primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: rgba(93, 114, 148, 0.42);
  border-color: rgba(255, 255, 255, 0.08);
  box-shadow: none;
}

.auth-arrow-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: rgba(186, 201, 230, 0.76);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.25s ease;
  background: none;
  border: none;
  padding: 0;
  margin-bottom: 12px;
}

.auth-arrow-link:hover {
  color: #7dd3fc;
}

@media (prefers-reduced-motion: reduce) {
  .auth-form,
  .custom-button.primary {
    animation: none !important;
    transition: none !important;
    transform: none !important;
    opacity: 1 !important;
  }
}

@media (max-width: 640px) {
  .auth-shell__content {
    align-items: center;
    padding: clamp(2rem, 9vw, 3rem) clamp(1rem, 6vw, 1.5rem);
  }

  .auth-form__header .main-title {
    font-size: clamp(1.9rem, 8vw, 2.3rem);
  }
}
</style>

